import {
  type ComponentProps,
  createContext,
  useContext,
  useState,
} from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Label } from './label';
import { cn } from './helpers';
import { inputVariants } from './input';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { ArrowDown2 } from 'iconsax-react';

export type FilterValuesContextType<T> =
  | {
      value: T[];
      setValue: (value: T[]) => void;
      multiple: true;
    }
  | {
      value: T;
      setValue: (value: T) => void;
      multiple: false | undefined;
    };

export const FilterValuesContext = createContext<
  FilterValuesContextType<unknown>
>({
  value: '',
  setValue: () => {},
  multiple: false,
});

const Filter = <T, M extends boolean>({
  multiple,
  value: passedValue,
  children,
  onValueChange,
  ...props
}: ComponentProps<typeof DropdownMenu> & {
  multiple?: M;
  value?: M extends true ? T[] : T;
  onValueChange: (values: M extends true ? T[] : T) => void;
}) => {
  const [value, setValuesInternal] = useState<T | T[]>(
    passedValue ?? (multiple ? [] : ('' as T))
  );

  const setValue = (v: M extends true ? T[] : T) => {
    setValuesInternal(v);
    onValueChange(v);
  };

  const contextValue = multiple
    ? {
        value,
        setValue: setValue,
        multiple: true,
      }
    : {
        value,
        setValue: setValue,
        multiple: false,
      };

  return (
    <FilterValuesContext.Provider
      value={contextValue as FilterValuesContextType<unknown>}
    >
      <DropdownMenu {...props}>
        <div>{children}</div>
      </DropdownMenu>
    </FilterValuesContext.Provider>
  );
};

const FilterClearBtn = ({
  children,
  className,
  onClearClick,
  ...props
}: ComponentProps<'button'> & {
  onClearClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
  const { value, setValue, multiple } = useContext(FilterValuesContext);

  const hasValues = multiple ? value.length > 0 : !!value;

  if (!hasValues) return null;

  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (multiple) {
          setValue([]);
        } else {
          setValue('');
        }
        onClearClick?.(e);
      }}
      className={cn(
        'text-gray-600 cursor-pointer hover:underline text-xs',
        className
      )}
      {...props}
    >
      {children || 'Clear'}
    </button>
  );
};

const FilterLabel = ({ className, ...props }: ComponentProps<typeof Label>) => {
  return (
    <Label
      className={cn('mb-1', className)}
      {...props}
    />
  );
};

const FilterTrigger = ({
  className,
  children,
  withArrow = true,
  asChild,
  ...props
}: ComponentProps<'button'> & {
  withArrow?: boolean;
  asChild?: boolean;
}) => {
  if (asChild) {
    return (
      <DropdownMenuTrigger
        asChild
        className={className}
        {...props}
      >
        {children}
      </DropdownMenuTrigger>
    );
  }

  return (
    <DropdownMenuTrigger
      className={cn(
        'w-full relative cursor-pointer items-center',
        inputVariants(),
        className
      )}
      {...props}
    >
      {children}

      {withArrow && (
        <ArrowDown2
          size={20}
          className='text-primary-1000 absolute right-2 top-1/2 -translate-y-1/2'
        />
      )}
    </DropdownMenuTrigger>
  );
};

const FilterValue = <T,>({
  className,
  children,
  ...props
}: Omit<ComponentProps<'span'>, 'children'> & {
  children?: (value: T) => React.ReactNode;
}) => {
  // Use non-generic useContext to avoid type errors, then assert type as needed
  const { multiple, value } = useContext(
    FilterValuesContext
  ) as FilterValuesContextType<T>;

  const hasValues = multiple ? value.length > 0 : !!value;

  if (!hasValues) return null;

  const childrenEl = multiple
    ? value.map((v) => children?.(v))
    : children?.(value);

  const displayedValue = multiple
    ? value.join(', ')
    : formatTextForDisplay(String(value));

  return (
    <span
      className={cn('text-sm', className)}
      {...props}
    >
      {children ? childrenEl : displayedValue}
    </span>
  );
};

const FilterOptionsList = <T,>({
  className,
  children,
  ...props
}: Omit<ComponentProps<typeof DropdownMenuContent>, 'children'> & {
  children?:
    | React.ReactNode
    | ((props: FilterValuesContextType<T>) => React.ReactNode);
}) => {
  const context = useContext(FilterValuesContext) as FilterValuesContextType<T>;
  return (
    <DropdownMenuContent
      className={cn('flex flex-col text-start text-sm', className)}
      {...props}
    >
      {typeof children === 'function' ? children(context) : children}
    </DropdownMenuContent>
  );
};

const FilterPlaceholder = ({ className, ...props }: ComponentProps<'span'>) => {
  const { value, multiple } = useContext(FilterValuesContext);

  const hasValues = multiple ? value.length > 0 : !!value;

  if (hasValues) return null;

  return (
    <span
      className={cn('text-gray-400 text-sm', className)}
      {...props}
    />
  );
};

const FilterOptionItem = <T,>({
  value: passedValue,
  selected,
  className,
  preventClose,
  ...props
}: ComponentProps<typeof DropdownMenuItem> & {
  value: T;
  selected?: boolean;
  preventClose?: boolean;
}) => {
  const { value, setValue, multiple } = useContext(
    FilterValuesContext
  ) as FilterValuesContextType<T>;

  const isSelected = multiple
    ? (value as T[]).some(
        (v) => JSON.stringify(v) === JSON.stringify(passedValue)
      )
    : JSON.stringify(value) === JSON.stringify(passedValue);

  return (
    <DropdownMenuItem
      data-selected={selected || isSelected}
      onSelect={(event) => {
        if (preventClose) {
          event.preventDefault();
        }
      }}
      onClick={(event) => {
        if (preventClose) {
          event.preventDefault();
          event.stopPropagation();
        }
        if (multiple) {
          if (isSelected) {
            setValue(
              value.filter(
                (v) => JSON.stringify(v) !== JSON.stringify(passedValue)
              )
            );
          } else {
            setValue([...value, passedValue]);
          }
        } else {
          setValue(passedValue);
        }
      }}
      className={cn(
        'flex items-center gap-2 data-[selected=true]:bg-primary-50',
        className
      )}
      {...props}
    />
  );
};

export {
  Filter,
  FilterClearBtn,
  FilterLabel,
  FilterTrigger,
  FilterValue,
  FilterOptionsList,
  FilterPlaceholder,
  FilterOptionItem,
};
